import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus } from '@fortawesome/pro-solid-svg-icons/faPlus';
import React, { useState, useCallback, useRef } from 'react';
import { FormattedMessage } from 'react-intl';
import { useSelector } from 'react-redux';

import { Button } from 'ui-components/components/button';
import { Popover } from 'ui-components/components/popover';
import { noop } from 'ui-components/default-props/noop';
import { NotificationService, NOTIFICATION_DURATIONS, NOTIFICATION_TYPE_ERROR } from 'ui-components/services/notification-service';
import { THEME_CONSTANTS } from 'ui-components/services/button-theme-service';

import { messages } from './messages';
import Global from '../../../../../../../../src/store/global-selectors';
import { addInventoryForProduct } from '../../../../../../../../src/store/inventory/inventory.api';
import { CostInput } from '../../shared-components/cost-input';
import { PopoverNotesInput } from '../../shared-components/popover-notes';
import { QuantityInput } from '../../shared-components/quantity-input';
import sharedStyle from '../style.module.css';
import style from './style.module.css';

/**
 * Component for displaying inventory add value popover
 * @param {object} props
 * @param {string} props.inventoryLocationId
 * @param {() => void} props.onReloadLocations
 * @param {string} props.productId
 * @param {boolean} props.isPopoverOpen
 * @param {() => void} props.onClose
 * @param {string} [props.lotUuid]
 * @returns {JSX.Element}
 */
export const AddInventoryPopover = ({ inventoryLocationId, onReloadLocations, productId, isPopoverOpen, onClose = noop, lotUuid = '' }) => {
  const sellerCurrencyCode = useSelector(Global.selectSellerDefaultCurrency);
  const [isOpen, setIsOpen] = useState(isPopoverOpen);
  const [quantity, setQuantity] = useState(0);
  const [cost, setCost] = useState(0);
  const [notes, setNotes] = useState('');
  // TODO: extract isSubmittingRef + isSubmitting guard into a shared useInventorySubmit hook
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isSubmittingRef = useRef(false);
  const clearInputs = useCallback(() => {
    setQuantity(0);
    setCost(0);
    setNotes('');
  }, [setQuantity, setCost, setNotes]);
  const onOpenPopover = useCallback(() => {
    clearInputs();
    setIsOpen(!isOpen);
  }, [clearInputs, setIsOpen, isOpen]);
  const onOuterAction = useCallback(() => {
    setIsOpen(false);
    onClose();
  }, [setIsOpen, onClose]);
  const onAddInventory = useCallback(() => {
    if (isSubmittingRef.current) {
      return;
    }

    isSubmittingRef.current = true;
    setIsSubmitting(true);

    const addProps = {
      productId,
      inventoryLocationId,
      quantity,
      cost: {
        value: cost,
        code: sellerCurrencyCode,
      },
      description: notes,
      lotUuid,
    };
    const addInventoryPromise = addInventoryForProduct(addProps);
    addInventoryPromise.promise
      .then(() => {
        onReloadLocations();
        setIsOpen(false);
      })
      .catch(() => {
        NotificationService.showNotification({
          notificationType: NOTIFICATION_TYPE_ERROR,
          title: <FormattedMessage {...messages.addInventoryErrorTitle} />,
          content: <FormattedMessage {...messages.addInventoryErrorMessage} />,
          duration: NOTIFICATION_DURATIONS.SHORT,
        });
      })
      .finally(() => {
        isSubmittingRef.current = false;
        setIsSubmitting(false);
      });
  }, [productId, inventoryLocationId, quantity, cost, sellerCurrencyCode, notes, onReloadLocations, lotUuid]);

  const addInventoryPopopver = (
    <div data-ignore-root-focus>
      <div className={sharedStyle['popover-title']}>
        <FormattedMessage {...messages.addInventoryPopOverTitle} />
      </div>
      <div className={sharedStyle['popover-body']}>
        <div className={style['body-inputs']}>
          <QuantityInput onChange={setQuantity} quantity={quantity} />
          <CostInput cost={cost} setCost={setCost} />
          <PopoverNotesInput notes={notes} setNotes={setNotes} />
        </div>
      </div>
      <div className={sharedStyle['popover-buttons']}>
        <Button onClick={onOuterAction} theme={THEME_CONSTANTS.tertiary}>
          <FormattedMessage {...messages.inventoryPopoverCancel} />
        </Button>
        <Button disabled={!quantity || isSubmitting} onClick={onAddInventory} theme={THEME_CONSTANTS.primary}>
          <FormattedMessage {...messages.inventoryPopoverAdd} />
        </Button>
      </div>
    </div>
  );

  const popoverProps = {
    isOpen,
    preferPlace: 'below',
    enterExitTransitionDurationMs: 0,
    body: addInventoryPopopver,
    appendTarget: document.querySelector('#app-root'),
    onOuterAction,
  };

  return (
    <Popover {...popoverProps}>
      <Button className={sharedStyle['adjust-icon']} onClick={onOpenPopover} theme={THEME_CONSTANTS.link}>
        {!isPopoverOpen && <FontAwesomeIcon className={sharedStyle.icon} icon={faPlus} />}
      </Button>
    </Popover>
  );
};
